import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

const defaultPages = [
  { value: 'home', label: 'Home Page' },
  { value: 'about', label: 'About Page' },
  { value: 'find-space', label: 'Find a Space' },
  { value: 'blog', label: 'Blog Listing' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'contact', label: 'Contact' }
];

export default function AdminSEOSettings() {
  const [seoSettings, setSeoSettings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    page: '',
    title: '',
    meta_description: '',
    og_title: '',
    og_description: '',
    og_image: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/seo-settings');
      setSeoSettings(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/admin/seo-settings/${formData.page}`, formData);
      toast.success('SEO settings saved successfully!');
      setIsModalOpen(false);
      resetForm();
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save SEO settings');
    }
  };

  const handleEdit = (settings) => {
    setEditingPage(settings.page);
    setFormData(settings);
    setIsModalOpen(true);
  };

  const handleDelete = async (page) => {
    if (window.confirm('Delete SEO settings for this page?')) {
      try {
        await apiClient.delete(`/admin/seo-settings/${page}`);
        toast.success('SEO settings deleted!');
        fetchSettings();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const handleAddNew = (pageValue) => {
    setFormData({
      page: pageValue,
      title: '',
      meta_description: '',
      og_title: '',
      og_description: '',
      og_image: ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      page: '',
      title: '',
      meta_description: '',
      og_title: '',
      og_description: '',
      og_image: ''
    });
    setEditingPage(null);
  };

  const getPageLabel = (pageValue) => {
    const page = defaultPages.find(p => p.value === pageValue);
    return page ? page.label : pageValue;
  };

  const getAvailablePages = () => {
    const usedPages = seoSettings.map(s => s.page);
    return defaultPages.filter(p => !usedPages.includes(p.value));
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">SEO Management</h1>
              <p className="text-gray-600 mt-1">Manage page titles, meta descriptions, and Open Graph tags</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Available Pages */}
        {getAvailablePages().length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Add SEO for:</h3>
            <div className="flex flex-wrap gap-2">
              {getAvailablePages().map((page) => (
                <Button
                  key={page.value}
                  size="sm"
                  onClick={() => handleAddNew(page.value)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-1" />
                  {page.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Existing SEO Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {seoSettings.map((settings) => (
            <div key={settings.page} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{getPageLabel(settings.page)}</h3>
                  <span className="text-xs text-gray-500">/{settings.page}</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleEdit(settings)} size="sm" variant="outline">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(settings.page)} size="sm" variant="destructive">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Title:</span>
                  <p className="text-gray-600 truncate">{settings.title || '(Not set)'}</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Meta Description:</span>
                  <p className="text-gray-600 line-clamp-2">{settings.meta_description || '(Not set)'}</p>
                </div>
                {settings.og_image && (
                  <div>
                    <span className="font-semibold text-gray-700">OG Image:</span>
                    <img src={settings.og_image} alt="OG" className="mt-1 w-20 h-20 object-cover rounded" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {seoSettings.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <Search size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No SEO settings configured yet. Add one above!</p>
          </div>
        )}
      </div>

      {/* SEO Settings Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? `Edit SEO - ${getPageLabel(editingPage)}` : 'Add SEO Settings'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Page</Label>
              <Input
                value={getPageLabel(formData.page)}
                disabled
                className="bg-gray-100"
              />
            </div>

            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Basic SEO</h3>
              <div className="space-y-3">
                <div>
                  <Label>Page Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="The Circuit - Executive Coworking Space"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Appears in browser tab and search results (50-60 characters recommended)</p>
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Premium executive coworking spaces in Bangalore..."
                    rows={3}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Search result snippet (150-160 characters recommended)</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Open Graph / Social Media (Optional)</h3>
              <div className="space-y-3">
                <div>
                  <Label>OG Title</Label>
                  <Input
                    value={formData.og_title}
                    onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                    placeholder="Leave empty to use Page Title"
                  />
                  <p className="text-xs text-gray-500 mt-1">Title when shared on social media</p>
                </div>
                <div>
                  <Label>OG Description</Label>
                  <Textarea
                    value={formData.og_description}
                    onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                    placeholder="Leave empty to use Meta Description"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">Description when shared on social media</p>
                </div>
                <ImageUpload
                  label="OG Image"
                  value={formData.og_image}
                  onChange={(url) => setFormData({ ...formData, og_image: url })}
                  preview={true}
                />
                <p className="text-xs text-gray-500">Image shown when page is shared (1200x630px recommended)</p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save SEO Settings
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
